import { listSourceConfigs } from 'adflow';

import {
  isTemplateBuilderOperation,
  operationDomain,
  operationSummary,
  operationSupported,
  TEMPLATE_BUILDER_OPERATIONS,
  type OperationAvailability,
} from './operations';

export const REFERENCE_KINDS = [
  'operation',
  'element_type',
  'display_options',
  'source_family',
  'record_shape',
] as const;

export type ReferenceKind = (typeof REFERENCE_KINDS)[number];

export type ReferenceEntry = {
  entryId: string;
  kind: ReferenceKind;
  title: string;
  summary: string;
  payload: unknown;
};

const ELEMENT_TYPES: ReferenceEntry[] = [
  ['SVG', 'SVG layer. Editable markup lives on data.content when it is not a URL.'],
  ['TEXT', 'Text layer. Content, style, dynamic font, date, odds, and penalties fields.'],
  ['IMAGE', 'Image layer. Content is a URL or a data-point token, not returned on document read.'],
  ['TEAM_ASSET', 'Team asset layer. Jersey, badge, or custom asset content.'],
  [
    'CASINO_ASSET',
    'Casino asset layer. Source is SQUARE_IMAGE, LANDSCAPE_IMAGE, or PORTRAIT_IMAGE.',
  ],
  ['VECTOR', 'Vector layer.'],
].map(([entryId, summary]) => ({
  entryId,
  kind: 'element_type' as const,
  title: entryId,
  summary,
  payload: { type: entryId, fields: ['id', 'name', 'position', 'data', 'sources'] },
}));

const DISPLAY_OPTIONS: ReferenceEntry[] = [
  {
    entryId: 'teamAssets',
    kind: 'display_options',
    title: 'Team assets',
    summary: 'Template display option for team asset type and fallback.',
    payload: {
      key: 'teamAssets',
      type: ['BADGE', 'JERSEY', 'CUSTOM'],
      fallback: ['NONE', 'BADGE', 'JERSEY'],
    },
  },
  {
    entryId: 'odds',
    kind: 'display_options',
    title: 'Odds format',
    summary: 'Template or element odds display.',
    payload: {
      key: 'odds',
      type: [
        'europeanDecimalOdds',
        'americanOdds',
        'decimalOdds',
        'fractionalOdds',
        'impliedProbability',
        'potentialWinnings',
      ],
    },
  },
  {
    entryId: 'dateTimeFormat',
    kind: 'display_options',
    title: 'Date format',
    summary: 'Template or element date and countdown display.',
    payload: {
      key: 'dateTimeFormat',
      timeFormat: ['hour12', 'hour24'],
      monthFormat: ['long', 'short'],
      dayFormat: ['long', 'short'],
    },
  },
  {
    entryId: 'penaltiesFormat',
    kind: 'display_options',
    title: 'Penalties format',
    summary: 'Element penalties separator.',
    payload: { key: 'penaltiesFormat', fields: ['separator'] },
  },
];

const RECORD_SHAPES: ReferenceEntry[] = [
  {
    entryId: 'data_point',
    kind: 'record_shape',
    title: 'Data point binding',
    summary:
      'Shape of one element data-point binding. The live list for this template is template_builder_read data_points.',
    payload: {
      fields: ['id', 'display', 'sourceName', 'sourceType'],
      binding: { sourceId: 'string', name: 'string' },
    },
  },
  {
    entryId: 'advertiser',
    kind: 'record_shape',
    title: 'Advertiser record',
    summary: 'Shape of an advertiser row. The live list is template_builder_read advertisers.',
    payload: { fields: ['id', 'name', 'type'] },
  },
  {
    entryId: 'brand',
    kind: 'record_shape',
    title: 'Brand payload',
    summary: 'Shape of the loaded brand summary. The live value is template_builder_read brand.',
    payload: {
      fields: [
        'name',
        'brandKitId',
        'brandKitName',
        'colors',
        'colorCount',
        'fontFamilies',
        'fonts',
        'logoPresent',
      ],
    },
  },
];

function operationEntries(availability: OperationAvailability): ReferenceEntry[] {
  return TEMPLATE_BUILDER_OPERATIONS.filter(
    (operation) => operationDomain(operation) !== 'animation' || availability.animationsEnabled,
  ).map((operation) => ({
    entryId: operation,
    kind: 'operation' as const,
    title: operation,
    summary: operationSummary(operation),
    payload: {
      operation,
      domain: operationDomain(operation),
      supported: operationSupported(operation),
      args: operationSummary(operation),
    },
  }));
}

function sourceFamilyEntries(): ReferenceEntry[] {
  return listSourceConfigs().map((config) => ({
    entryId: config.id,
    kind: 'source_family' as const,
    title: config.name,
    summary: `${config.name} template types: ${config.templateTypes.join(', ') || 'none'}.`,
    payload: {
      id: config.id,
      name: config.name,
      templateTypes: config.templateTypes,
      marketTypes: config.marketTypes,
      multipleMarkets: config.multipleMarkets,
      acceptEmptyMarkets: config.acceptEmptyMarkets,
      needsOddsFormatting: config.needsOddsFormatting ?? false,
    },
  }));
}

export function referenceEntries(
  availability: OperationAvailability = { animationsEnabled: false },
): ReferenceEntry[] {
  return [
    ...operationEntries(availability),
    ...ELEMENT_TYPES,
    ...DISPLAY_OPTIONS,
    ...sourceFamilyEntries(),
    ...RECORD_SHAPES,
  ];
}

export function isReferenceKind(value: string): value is ReferenceKind {
  return (REFERENCE_KINDS as readonly string[]).includes(value);
}

export function searchReference(input: {
  query?: string;
  entryId?: string;
  kind?: string;
  limit?: number;
  includePayload?: boolean;
  availability?: OperationAvailability;
}):
  | { entries: Array<Omit<ReferenceEntry, 'payload'> & { payload?: unknown }> }
  | { error: string } {
  let kind = input.kind;
  let entryId = input.entryId;
  if (kind && isTemplateBuilderOperation(kind)) {
    entryId = entryId ?? kind;
    kind = 'operation';
  }
  if (kind && !isReferenceKind(kind)) {
    return {
      error: `Unsupported reference kind "${kind}". Live catalogs such as advertisers stay on template_builder_read.`,
    };
  }

  const limit = input.limit && input.limit > 0 ? Math.min(input.limit, 20) : 8;
  let entries = referenceEntries(input.availability);
  if (kind && isReferenceKind(kind)) {
    entries = entries.filter((entry) => entry.kind === kind);
  }
  if (entryId) {
    entries = entries.filter((entry) => entry.entryId === entryId);
  }
  if (input.query) {
    const query = input.query.toLowerCase();
    entries = entries.filter((entry) =>
      `${entry.entryId} ${entry.title} ${entry.summary}`.toLowerCase().includes(query),
    );
  }

  return {
    entries: entries.slice(0, limit).map((entry) => {
      if (input.includePayload === false && !input.entryId) {
        const { payload: _payload, ...rest } = entry;
        return rest;
      }
      return entry;
    }),
  };
}
